import { useAppDispatch, useAppSelector } from "@/app/store/store";
import axios from "axios";
import { append, IsLoading, merge, pop, reset, setLoading } from "./slice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Admin } from "@/savaradio/common";
import { useStoreRealtime } from "../../../realtime/hook";
import { useStoreSession } from "../../../session/hook";
import { PartialItem } from "./util";

const url = process.env.NEXT_PUBLIC_URL_BACK || "http://localhost:3000";

export function useStoreAdmin(init_data?: boolean, init_socket?: boolean) {
  const dispatch = useAppDispatch();
  const admins = useAppSelector((state) => state.admin_slice.value);
  const isLoading = useAppSelector((state) => state.admin_slice.isLoading);

  const { connect, status, emit, signin } = useStoreRealtime();
  const { session } = useStoreSession();
  const admin_status = status("admin");
  const [progress, setProgress] = useState<number>(0);
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);

  const setIsLoading = useCallback(
    (p: Partial<IsLoading>) => {
      dispatch(setLoading(p));
    },
    [dispatch],
  );

  const get_admin = useCallback(async () => {
    if (isLoading.get) return;
    setIsLoading({ get: true });
    try {
      const res = await axios.get("/api/crud/admin", { responseType: "json" });
      dispatch(reset(res?.data || []));
    } catch {
      dispatch(reset([]));
    } finally {
      setIsLoading({ get: false });
    }
  }, [setIsLoading, isLoading.get, dispatch]);

  const create = useCallback(
    async (admin: PartialItem<Admin, "_id" | "token" | "validated">) => {
      setIsLoading({ create: true });
      try {
        const { data } = await axios.post("/api/crud/admin", admin, {
          responseType: "json",
        });
        if (data) {
          dispatch(append(data));
          axios
            .post("/api/auth/email/usercreation", {
              id: data._id,
              email: admin.email,
              name: admin.username,
              type: "admin",
              link: `${url}/auth/pass`,
            })
            .then((res) => console.log(res.data));
          if (admin_status === "signed") emit("admin", "create", data);
          return data;
        }
        return null;
      } catch {
        return null;
      } finally {
        setIsLoading({ create: false });
      }
    },
    [setIsLoading, admin_status, emit, dispatch],
  );

  const remove = useCallback(
    async (id: string) => {
      setIsLoading({ delete: true });
      try {
        const { data } = await axios.delete(`/api/crud/admin/${id}`, {
          responseType: "json",
        });
        if (data) {
          dispatch(pop(data));
          if (admin_status === "signed") emit("admin", "delete", data);
          return data;
        }
        return null;
      } catch {
        return null;
      } finally {
        setIsLoading({ delete: false });
      }
    },
    [setIsLoading, admin_status, emit, dispatch],
  );

  const update = useCallback(
    async (id: string, upd: Partial<Admin>) => {
      setIsLoading({ update: true });
      try {
        const { data } = await axios.patch(`/api/crud/admin/${id}`, upd, {
          responseType: "json",
        });
        if (data) {
          const new_data = { ...data, ...upd };
          dispatch(merge(new_data));
          if (admin_status === "signed") emit("admin", "update", new_data);
          return new_data;
        }
        return null;
      } catch {
        return null;
      } finally {
        setIsLoading({ update: false });
      }
    },
    [setIsLoading, admin_status, emit, dispatch],
  );

  const upload = useCallback(async (file: File) => {
    setIsLoading({ upload: true });
    if (file.size === 0) return null;
    try {
      const abort = new AbortController();
      setAbortController(abort);
      const { data } = await axios.get(
        `/api/minio?type=${encodeURIComponent(file.type)}&name=${encodeURIComponent(file.name)}&folder=${encodeURIComponent("images/avatar/admin")}`,
        { signal: abort.signal },
      );

      if (data?.signedUrl && data?.path) {
        try {
          const { status } = await axios.put(data.signedUrl, file, {
            signal: abort.signal,
            onUploadProgress: (e) => {
              if (e.total) {
                const percentCompleted = Math.round((e.loaded * 100) / e.total);
                setProgress(percentCompleted);
              }
            },
          });
          return status === 200 || status === 201 ? data.path : null;
        } catch (err) {
          return null;
        }
      }
      return null;
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log("Upload annulé :", file.name);
        setAbortController(null);
      }
      return null;
    } finally {
      setAbortController(null);
      setIsLoading({ upload: false });
    }
  }, []);

  const cancelRequest = useCallback(() => {
    abortController?.abort();
  }, [abortController]);

  const initializing = useMemo(() => {
    return isLoading.get && (!admins || admins.length === 0);
  }, [isLoading.get, admins]);

  const find_sync = useCallback(
    (id: string) => {
      return admins?.find((admin) => admin._id === id);
    },
    [admins],
  );

  useEffect(() => {
    if (init_data && !admins && !isLoading.get) {
      get_admin();
    }
  }, [init_data, admins, get_admin, isLoading.get]);

  useEffect(() => {
    if (init_socket && admin_status === "disconnected") {
      connect("admin");
    }
  }, [init_socket, admin_status, connect]);

  useEffect(() => {
    if (session && admin_status === "connected") {
      signin("admin", session);
    }
  }, [session, admin_status, signin]);

  return useMemo(
    () => ({
      admins,
      isLoading,
      initializing,
      progress,
      cancelRequest,
      crud: { create, remove, update, upload, get: get_admin },
      find: { sync: find_sync },
    }),
    [
      admins,
      isLoading,
      initializing,
      progress,
      create,
      remove,
      update,
      upload,
      get_admin,
      find_sync,
      cancelRequest,
    ],
  );
}
